import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, TrendingUp, TrendingDown, ArrowRight, AlertTriangle, Search } from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, Legend,
  ResponsiveContainer, CartesianGrid,
} from "recharts";
import useAuthStore from "@/store/authStore";
import {
  C, fmtInt, fmtPct, fmtDelta, heatBg, heatFg, VISIBLE_CAP,
  SplitCard, ParetoCard,
} from "./ForecastSummary";

const API = process.env.REACT_APP_BACKEND_URL;
const ALL = "__ALL__";

// Aug 2026 (weekly split grouped-bar chart) — tooltip content lifted
// to module scope so React's reconciler treats it as a stable
// component reference (avoids the `no-unstable-nested-components`
// warning + the destroy/rebuild churn of an inline arrow).
function WeeklySplitTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const w = payload[0].payload || {};
  const planned = Number(w.planned || 0);
  const completed = Number(w.completed || 0);
  const attainment = planned > 0 ? (completed / planned) * 100 : 0;
  return (
    <div className="bg-white border border-zinc-200 rounded-sm px-2.5 py-1.5 text-xs shadow-sm" data-testid="ps-weekly-tooltip">
      <div className="font-medium mb-1">{label} · <span className="font-mono text-zinc-500">{w.start} → {w.end}</span></div>
      <div className="grid grid-cols-[10px_1fr_auto] gap-x-1.5 gap-y-0.5 items-center">
        <span className="inline-block w-2 h-2" style={{ background: C.data }} />
        <span className="text-zinc-500">Planned</span>
        <span className="font-mono">{fmtInt(planned)}</span>
        <span className="inline-block w-2 h-2" style={{ background: C.primary }} />
        <span className="text-zinc-500">Completed</span>
        <span className="font-mono">{fmtInt(completed)}</span>
        <span />
        <span className="text-zinc-500 pt-0.5 border-t border-zinc-100">Attainment</span>
        <span className="font-mono pt-0.5 border-t border-zinc-100">{fmtPct(attainment)}</span>
      </div>
    </div>
  );
}

// Feb 2026 — CPC "Production Summary" tab. Production-plan counterpart
// of the Demand Forecasts Summary tab. Every number comes from a
// single weekly snapshot (production_snapshots) — no live re-aggregation
// on load. Reuses the Forecast Summary component pattern (SplitCard,
// ParetoCard, C tokens, heatmap ramp).
export default function ProductionSummary() {
  const { token } = useAuthStore();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [branch, setBranch] = useState(ALL);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [othersMatrixOpen, setOthersMatrixOpen] = useState(false);
  const [unmappedOpen, setUnmappedOpen] = useState(false);

  // Load active branches for the scope picker.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/branches?is_active=true`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => {
      if (cancelled) return;
      const arr = Array.isArray(r.data) ? r.data : (r.data.items || r.data.branches || []);
      setBranches(arr.filter((b) => b.is_active !== false));
    }).catch(() => { /* selector degrades to All Branches only */ });
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    if (!month) return;
    let cancelled = false;
    setLoading(true); setError("");
    axios.get(`${API}/api/cpc/production-summary?month=${month}&branch=${encodeURIComponent(branch)}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => {
      if (cancelled) return;
      setData(r.data);
    }).catch((e) => {
      if (cancelled) return;
      setError(e?.response?.data?.detail || e.message || "Failed to load");
    }).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [month, branch, token]);

  const isEmpty = data && (data.empty || (data.headline?.total_planned || 0) === 0);
  const bridge = useMemo(() => {
    if (!data?.movement) return null;
    const m = data.movement;
    const adds = m.additions.reduce((a, r) => a + (r.units || 0), 0);
    const drops = m.dropouts.reduce((a, r) => a + (r.units || 0), 0);
    return { prior: m.prior_total, adds, drops, net: m.net_revisions, curr: m.current_total,
             reconciles: Math.abs((m.prior_total + adds - drops + m.net_revisions) - m.current_total) < 0.01 };
  }, [data]);

  const matrixMaxCell = useMemo(() => {
    if (!data?.matrix?.cells?.length) return 0;
    const isOthersLast = data.matrix.brands?.[data.matrix.brands.length - 1] === "Others";
    const stop = isOthersLast ? data.matrix.brands.length - 1 : data.matrix.brands.length;
    let m = 0;
    for (const row of data.matrix.cells) {
      for (let j = 0; j < stop; j++) if (row[j] > m) m = row[j];
    }
    return m;
  }, [data]);

  return (
    <div className="space-y-6" data-testid="production-summary-tab">
      {/* Control bar */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div className="flex items-end gap-3">
          <div>
            <Label className="text-xs uppercase text-zinc-500">Month</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                   className="w-[180px] rounded-sm" data-testid="ps-month-picker" />
          </div>
          <div>
            <Label className="text-xs uppercase text-zinc-500">Branch</Label>
            <Select value={branch} onValueChange={setBranch}>
              <SelectTrigger className="w-[220px] rounded-sm" data-testid="ps-branch-select">
                <SelectValue placeholder="All Branches" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL} data-testid="ps-branch-opt-all">All Branches</SelectItem>
                {branches.map((b) => (
                  <SelectItem key={b.id || b.name} value={b.name} data-testid={`ps-branch-opt-${b.name}`}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="text-right text-xs text-zinc-500">
          <div className="uppercase tracking-wide text-[10px]" data-testid="ps-cadence-caption">as of today 06:00 IST</div>
          {data?.snapshot_meta?.current_snapshot_date && (
            <div>
              Latest week-close: <span className="font-mono">{data.snapshot_meta.current_snapshot_date}</span>
              {data.snapshot_meta.current_is_provisional && (
                <span className="ml-1 inline-block px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-900 text-[10px]" data-testid="ps-provisional-badge">provisional / WTD</span>
              )}
              {data.snapshot_meta.prior_snapshot_date && (
                <> · Prior: <span className="font-mono">{data.snapshot_meta.prior_snapshot_date}</span></>
              )}
            </div>
          )}
          {data?.snapshot_meta?.captured_week_closes?.length > 0 && (
            <div className="text-[10px]">Captured week-closes: <span className="font-mono">{data.snapshot_meta.captured_week_closes.join(", ")}</span></div>
          )}
        </div>
      </div>

      {loading && <div className="text-center py-12 text-zinc-500"><Loader2 className="inline w-4 h-4 animate-spin mr-2" />Loading…</div>}
      {error && <div className="border border-rose-300 bg-rose-50 text-rose-900 rounded-sm p-3 text-sm" data-testid="ps-error">{error}</div>}

      {!loading && data && (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <Card className="md:col-span-2 rounded-sm" data-testid="ps-headline-total">
              <CardHeader className="pb-2"><CardTitle className="text-xs uppercase text-zinc-500">Planned units — {month} · {branch === ALL ? "All Branches" : branch}</CardTitle></CardHeader>
              <CardContent>
                <div className="text-4xl font-black tracking-tight" data-testid="ps-planned-units">{fmtInt(data.headline.total_planned)}</div>
                {bridge && bridge.prior > 0 && (
                  <div className={`text-sm mt-1 ${bridge.curr >= bridge.prior ? "text-emerald-700" : "text-rose-700"}`}>
                    {bridge.curr >= bridge.prior ? <TrendingUp className="inline w-3.5 h-3.5 mr-1" /> : <TrendingDown className="inline w-3.5 h-3.5 mr-1" />}
                    {fmtDelta(bridge.curr - bridge.prior)} ({bridge.prior ? fmtPct((bridge.curr - bridge.prior) / bridge.prior * 100) : "—"}) vs last snapshot
                  </div>
                )}
                <div className="text-xs text-zinc-500 mt-1">
                  Completed <span className="font-mono font-semibold text-zinc-800">{fmtInt(data.headline.total_completed)}</span>
                  {" · "}Pending <span className="font-mono font-semibold text-zinc-800">{fmtInt(data.headline.pending)}</span>
                </div>
              </CardContent>
            </Card>
            {[
              { label: "Active buyer SKUs", value: data.headline.active_skus, tid: "ps-kpi-skus" },
              { label: "Verticals", value: data.headline.verticals, sub: `${data.pareto?.vertical?.top80_count || 0} make 80%`, tid: "ps-kpi-verticals" },
              { label: "Brands", value: data.headline.brands, sub: `${data.pareto?.brand?.top80_count || 0} make 80%`, tid: "ps-kpi-brands" },
              { label: "Weeks", value: data.headline.weeks, tid: "ps-kpi-weeks" },
              { label: "Branches", value: data.headline.branches, sub: (data.branch_rollup?.[0] ? `Top: ${data.branch_rollup[0].branch} ${fmtPct(data.branch_rollup[0].pct)}` : ""), tid: "ps-kpi-branches" },
            ].map((t) => (
              <Card key={t.label} className="rounded-sm" data-testid={t.tid}>
                <CardHeader className="pb-2"><CardTitle className="text-xs uppercase text-zinc-500">{t.label}</CardTitle></CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold tracking-tight">{fmtInt(t.value)}</div>
                  {t.sub && <div className="text-[11px] text-zinc-500 truncate">{t.sub}</div>}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Unmapped surfacing */}
          {data.unmapped && data.unmapped.sku_count > 0 && (
            <div className="border border-amber-300 bg-amber-50 rounded-sm px-3 py-2 text-xs flex items-center justify-between" data-testid="ps-unmapped-line">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-700" />
                <span className="text-amber-900">
                  Unmapped: <span className="font-mono font-semibold">{data.unmapped.sku_count}</span> SKUs
                  {" / "}<span className="font-mono font-semibold">{fmtInt(data.unmapped.units)}</span> units
                  {" "}(<span className="font-mono">{fmtPct(data.unmapped.pct)}</span> of scope) — brand/vertical could not be resolved off the buyer-SKU master.
                </span>
              </div>
              {data.unmapped.sample_ids?.length > 0 && (
                <button className="text-amber-900 underline hover:text-amber-950" onClick={() => setUnmappedOpen(true)} data-testid="ps-unmapped-view">
                  View sample →
                </button>
              )}
            </div>
          )}

          {isEmpty && (
            <Card className="rounded-sm"><CardContent className="py-12 text-center text-zinc-500" data-testid="ps-empty-state">
              No production schedules for this month{branch !== ALL ? ` at ${branch}` : ""} yet.
            </CardContent></Card>
          )}

          {!isEmpty && (
            <>
              {/* Section A — Plan snapshot */}
              <div>
                <h2 className="text-lg font-bold tracking-tight mb-2">
                  Plan Snapshot
                  <span className="text-xs text-zinc-500 font-normal ml-2">
                    {data.snapshot_meta.current_snapshot_date
                      ? <>as of <span className="font-mono">{data.snapshot_meta.current_snapshot_date}</span></>
                      : ""}
                  </span>
                </h2>

                {/* Branch roll-up (All-Branches only) */}
                {branch === ALL && data.branch_rollup?.length > 0 && (
                  <Card className="rounded-sm mb-4" data-testid="ps-branch-rollup">
                    <CardHeader className="pb-2"><CardTitle className="text-sm">Branch roll-up</CardTitle></CardHeader>
                    <CardContent className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-zinc-50">
                          <tr>
                            <th className="text-left px-3 py-2 uppercase tracking-wide text-[10px] text-zinc-500">Branch</th>
                            <th className="text-right px-3 py-2 uppercase tracking-wide text-[10px] text-zinc-500">Planned</th>
                            <th className="text-right px-3 py-2 uppercase tracking-wide text-[10px] text-zinc-500">%</th>
                            <th className="text-left  px-3 py-2 uppercase tracking-wide text-[10px] text-zinc-500">Week-shape</th>
                            <th className="text-right px-3 py-2 uppercase tracking-wide text-[10px] text-zinc-500">Completed</th>
                            <th className="text-right px-3 py-2 uppercase tracking-wide text-[10px] text-zinc-500">Pending</th>
                            <th className="text-right px-3 py-2 uppercase tracking-wide text-[10px] text-zinc-500">SKUs</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.branch_rollup.map((b) => (
                            <BranchRow key={b.branch} row={b} weeks={data.weeks || []} />
                          ))}
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>
                )}

                {/* Weekly split — Aug 2026: grouped Planned vs Completed
                    bars for plan-adherence visibility. Both series
                    come from the existing `weekly_split[]` payload
                    (`.planned` / `.completed`) — no backend change. */}
                <Card className="rounded-sm mb-4" data-testid="ps-weekly-split">
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Weekly split — ISO weeks (Mon–Sun, IST)</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={data.weekly_split} margin={{ left: 8, right: 12, top: 4 }} barGap={4} barCategoryGap="20%">
                        <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                        <XAxis dataKey="code" />
                        <YAxis tickFormatter={fmtInt} />
                        <Tooltip
                          cursor={{ fill: "rgba(39,39,42,0.04)" }}
                          content={<WeeklySplitTooltip />}
                        />
                        <Legend
                          wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
                          iconType="square"
                          iconSize={8}
                          formatter={(v) => (v === "planned" ? "Planned" : v === "completed" ? "Completed" : v)}
                        />
                        <Bar dataKey="planned" fill={C.data} radius={[3, 3, 0, 0]} data-testid="ps-weekly-bar-planned" />
                        <Bar dataKey="completed" fill={C.primary} radius={[3, 3, 0, 0]} data-testid="ps-weekly-bar-completed" />
                      </BarChart>
                    </ResponsiveContainer>
                    <table className="w-full text-xs mt-2" data-testid="ps-weekly-table">
                      <thead>
                        <tr>
                          <th className="text-left px-2 py-1 text-[10px] uppercase text-zinc-500">Week</th>
                          <th className="text-left px-2 py-1 text-[10px] uppercase text-zinc-500">Range</th>
                          <th className="text-right px-2 py-1 text-[10px] uppercase text-zinc-500">Planned</th>
                          <th className="text-right px-2 py-1 text-[10px] uppercase text-zinc-500">Completed</th>
                          <th className="text-right px-2 py-1 text-[10px] uppercase text-zinc-500">Attainment %</th>
                          <th className="text-right px-2 py-1 text-[10px] uppercase text-zinc-500">Share %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.weekly_split.map((w) => {
                          const planned = Number(w.planned || 0);
                          const completed = Number(w.completed || 0);
                          const attainment = planned > 0 ? (completed / planned) * 100 : 0;
                          return (
                            <tr key={w.code} className="border-t border-zinc-100">
                              <td className="px-2 py-1 font-medium">{w.code}</td>
                              <td className="px-2 py-1 font-mono text-zinc-500">{w.start} → {w.end}</td>
                              <td className="px-2 py-1 text-right font-mono tabular-nums">{fmtInt(planned)}</td>
                              <td className="px-2 py-1 text-right font-mono tabular-nums" data-testid={`ps-weekly-${w.code}-completed`}>{fmtInt(completed)}</td>
                              <td className="px-2 py-1 text-right font-mono tabular-nums" data-testid={`ps-weekly-${w.code}-attainment`}>{fmtPct(attainment)}</td>
                              <td className="px-2 py-1 text-right font-mono tabular-nums text-zinc-500">{fmtPct(w.pct)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>

                {/* Splits (reuse the Forecast Summary components) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SplitCard title="Vertical split" rows={data.vertical_split} total={data.headline.total_planned} tid="ps-vertical-split" barColor={C.data} />
                  <SplitCard title="Brand split"    rows={data.brand_split}    total={data.headline.total_planned} tid="ps-brand-split"    barColor={C.data} />
                </div>

                {/* Matrix — heat-shaded, top-N + Others */}
                <Card className="mt-4 rounded-sm" data-testid="ps-matrix">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Vertical × Brand</CardTitle>
                    <div className="text-[11px] text-zinc-500">Shaded by unit concentration. Top {VISIBLE_CAP} brands shown; the tail is folded into <span className="font-medium">Others</span> — click the header to expand.</div>
                  </CardHeader>
                  <CardContent className="overflow-x-auto">
                    <table className="text-xs border border-zinc-200 border-collapse w-full">
                      <thead>
                        <tr className="bg-zinc-50">
                          <th className="text-left px-2 py-1 border border-zinc-200 uppercase tracking-wide text-[10px] text-zinc-500">Vertical \ Brand</th>
                          {data.matrix.brands.map((b) => {
                            const isOthers = b === "Others";
                            return (
                              <th key={b} className={`px-2 py-1 border border-zinc-200 text-right uppercase tracking-wide text-[10px] ${isOthers ? "cursor-pointer hover:bg-zinc-100 text-orange-700 underline" : "text-zinc-500"}`}
                                  onClick={isOthers ? () => setOthersMatrixOpen(true) : undefined}
                                  data-testid={isOthers ? "ps-matrix-others-header" : undefined}
                              >{b}{isOthers && (data.matrix.others_brands?.length || 0) > 0 && <> <span className="font-normal">({data.matrix.others_brands.length})</span></>}</th>
                            );
                          })}
                          <th className="px-2 py-1 border border-zinc-200 text-right uppercase tracking-wide text-[10px] text-zinc-500 bg-zinc-100">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.matrix.verticals.map((v, i) => (
                          <tr key={v}>
                            <td className="px-2 py-1 border border-zinc-200 font-medium">{v}</td>
                            {data.matrix.brands.map((b, j) => {
                              const cell = data.matrix.cells[i][j];
                              const isOthers = b === "Others";
                              return (
                                <td key={b} className="px-2 py-1 border border-zinc-200 text-right font-mono"
                                    style={{ background: isOthers ? "#F4F4F5" : heatBg(cell, matrixMaxCell),
                                             color: isOthers ? C.fg : heatFg(cell, matrixMaxCell) }}>
                                  {cell > 0 ? fmtInt(cell) : ""}
                                </td>
                              );
                            })}
                            <td className="px-2 py-1 border border-zinc-200 text-right font-mono font-bold bg-zinc-50">{fmtInt(data.matrix.row_totals[i])}</td>
                          </tr>
                        ))}
                        <tr className="bg-zinc-100">
                          <td className="px-2 py-1 border border-zinc-200 font-bold uppercase tracking-wide text-[10px]">Total</td>
                          {data.matrix.col_totals.map((c, j) => (
                            <td key={j} className="px-2 py-1 border border-zinc-200 text-right font-mono font-bold">{fmtInt(c)}</td>
                          ))}
                          <td className="px-2 py-1 border border-zinc-200 text-right font-mono font-bold">{fmtInt(data.headline.total_planned)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </CardContent>
                </Card>

                {/* Pareto trio */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                  <ParetoCard title="Top 80% — Vertical" data={data.pareto.vertical} tid="ps-pareto-vertical" mono={false} />
                  <ParetoCard title="Top 80% — Brand"    data={data.pareto.brand}    tid="ps-pareto-brand"    mono={false} />
                  <ParetoCard title="Top 80% — Buyer SKU" data={data.pareto.sku}     tid="ps-pareto-sku"      mono={true} />
                </div>
              </div>

              {/* Section B — Movement */}
              <div>
                <h2 className="text-lg font-bold tracking-tight mb-2">Week-over-Week Movement
                  <span className="text-xs text-zinc-500 font-normal ml-2">
                    {data.snapshot_meta.current_snapshot_date
                      ? <>As of <span className="font-mono">{data.snapshot_meta.current_snapshot_date}</span>
                          {data.snapshot_meta.prior_snapshot_date && <> vs <span className="font-mono">{data.snapshot_meta.prior_snapshot_date}</span></>}</>
                      : "No snapshots yet"}
                  </span>
                </h2>
                {!data.snapshot_meta.current_snapshot_date ? (
                  <Card className="rounded-sm"><CardContent className="py-8 text-center text-zinc-500" data-testid="ps-no-snapshot">
                    No snapshots yet — the first one will be captured next Monday (IST).
                  </CardContent></Card>
                ) : !data.snapshot_meta.prior_snapshot_date ? (
                  <Card className="rounded-sm"><CardContent className="py-6 text-center text-zinc-500" data-testid="ps-single-snapshot">
                    Baseline captured on <span className="font-mono">{data.snapshot_meta.current_snapshot_date}</span> — no prior week to compare yet.
                  </CardContent></Card>
                ) : (
                  <>
                    <Card className="rounded-sm mb-3" data-testid="ps-bridge">
                      <CardContent className="pt-4 grid grid-cols-5 gap-2 text-center text-sm">
                        {[
                          { l: "Last snapshot", v: bridge?.prior, sign: "" },
                          { l: "+ Additions", v: bridge?.adds, sign: "+" },
                          { l: "− Dropouts", v: bridge?.drops, sign: "−" },
                          { l: "± Revisions", v: bridge?.net, sign: "±" },
                          { l: "Now", v: bridge?.curr, sign: "" },
                        ].map((c) => (
                          <div key={c.l}>
                            <div className="text-[10px] uppercase text-zinc-500">{c.l}</div>
                            <div className="font-mono font-semibold">{c.sign}{fmtInt(c.v || 0)}</div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>

                    {/* Weekly-planning-movement */}
                    <Card className="rounded-sm mb-3" data-testid="ps-weekly-movement">
                      <CardHeader className="pb-2"><CardTitle className="text-sm">Weekly planning movement — last snapshot → now</CardTitle></CardHeader>
                      <CardContent className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-zinc-50">
                            <tr>
                              <th className="text-left px-3 py-2 uppercase tracking-wide text-[10px] text-zinc-500">Week</th>
                              <th className="text-right px-3 py-2 uppercase tracking-wide text-[10px] text-zinc-500">Last</th>
                              <th className="text-right px-3 py-2 uppercase tracking-wide text-[10px] text-zinc-500">Now</th>
                              <th className="text-right px-3 py-2 uppercase tracking-wide text-[10px] text-zinc-500">Δ</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.movement.weekly_movement.map((w) => (
                              <tr key={w.code} className="border-t border-zinc-100">
                                <td className="px-3 py-1 font-medium">{w.code}</td>
                                <td className="px-3 py-1 text-right font-mono">{fmtInt(w.prior)}</td>
                                <td className="px-3 py-1 text-right font-mono">{fmtInt(w.current)}</td>
                                <td className={`px-3 py-1 text-right font-mono font-semibold ${w.delta > 0 ? "text-emerald-700" : w.delta < 0 ? "text-rose-700" : "text-zinc-500"}`}>
                                  {w.delta > 0 ? <TrendingUp className="inline w-3 h-3 mr-0.5" /> : w.delta < 0 ? <TrendingDown className="inline w-3 h-3 mr-0.5" /> : null}
                                  {fmtDelta(w.delta)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </CardContent>
                    </Card>

                    {/* Trajectory */}
                    <Card className="rounded-sm mb-3" data-testid="ps-trajectory">
                      <CardHeader className="pb-2"><CardTitle className="text-sm">Plan trajectory (per captured Monday)</CardTitle></CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={180}>
                          <LineChart data={data.trajectory}>
                            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                            <XAxis dataKey="snapshot_date" /><YAxis tickFormatter={fmtInt} />
                            <Tooltip formatter={(v) => fmtInt(v)} />
                            <Line type="monotone" dataKey="total_planned" stroke={C.primary} strokeWidth={2} dot={{ r: 3, fill: C.primary }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>

                    {/* Additions / Dropouts / Movers */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <MoverTable title="Additions" rows={data.movement.additions} valKey="units" tid="ps-additions" sign="+" tone="text-emerald-700" />
                      <MoverTable title="Dropouts"  rows={data.movement.dropouts}  valKey="units" tid="ps-dropouts"  sign="−" tone="text-rose-700" />
                      <MoverTable title="Major movers" rows={data.movement.movers} valKey="delta" tid="ps-movers" isDelta />
                    </div>
                    {Math.abs(data.movement.other_revisions_net) > 0.01 && (
                      <div className="text-[11px] text-zinc-500 text-right mt-1">
                        Other revisions, net: <span className="font-mono">{fmtDelta(data.movement.other_revisions_net)}</span>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Deep dive */}
              <DeepDive month={month} branch={branch} token={token} />
            </>
          )}
        </>
      )}

      {/* Others drill-down — Vertical × Brand matrix */}
      <Dialog open={othersMatrixOpen} onOpenChange={setOthersMatrixOpen}>
        <DialogContent className="max-w-lg" data-testid="ps-matrix-others-dialog">
          <DialogHeader><DialogTitle>Others — brand breakdown</DialogTitle></DialogHeader>
          <div className="text-xs text-zinc-500 mb-2">Brands folded into the &quot;Others&quot; column (tail of the top-{VISIBLE_CAP} cap). All totals still reconcile.</div>
          <div className="max-h-96 overflow-y-auto border border-zinc-200 rounded-sm">
            <table className="w-full text-xs">
              <thead className="bg-zinc-50 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 uppercase tracking-wide text-[10px] text-zinc-500">Brand</th>
                  <th className="text-right px-3 py-2 uppercase tracking-wide text-[10px] text-zinc-500">Units</th>
                  <th className="text-right px-3 py-2 uppercase tracking-wide text-[10px] text-zinc-500">% of scope</th>
                </tr>
              </thead>
              <tbody>
                {(data?.matrix?.others_brands || []).map((b) => (
                  <tr key={b.name} className="border-t border-zinc-200">
                    <td className="px-3 py-1.5">{b.name}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{fmtInt(b.units)}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{fmtPct(b.pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Unmapped sample */}
      <Dialog open={unmappedOpen} onOpenChange={setUnmappedOpen}>
        <DialogContent className="max-w-lg" data-testid="ps-unmapped-dialog">
          <DialogHeader><DialogTitle>Unmapped buyer SKUs — cleanup list</DialogTitle></DialogHeader>
          <div className="text-xs text-zinc-500 mb-2">These buyer SKUs have production plans but no brand/vertical resolvable off the master.</div>
          <div className="max-h-96 overflow-y-auto border border-zinc-200 rounded-sm">
            <table className="w-full text-xs">
              <thead className="bg-zinc-50 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 uppercase tracking-wide text-[10px] text-zinc-500">Buyer SKU</th>
                  <th className="text-right px-3 py-2 uppercase tracking-wide text-[10px] text-zinc-500">Units</th>
                </tr>
              </thead>
              <tbody>
                {(data?.unmapped?.sample_ids || []).map((r) => (
                  <tr key={r.sku_id} className="border-t border-zinc-200">
                    <td className="px-3 py-1.5 font-mono">{r.sku_id}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{fmtInt(r.units)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Branch row (with W1..Wn mini-shape) ───────────────────────────
function BranchRow({ row, weeks }) {
  const maxW = Math.max(1, ...(row.weeks || [0]));
  return (
    <tr className="border-t border-zinc-100">
      <td className="px-3 py-1.5 font-medium">{row.branch}</td>
      <td className="px-3 py-1.5 text-right font-mono">{fmtInt(row.planned)}</td>
      <td className="px-3 py-1.5 text-right font-mono text-zinc-500">{fmtPct(row.pct)}</td>
      <td className="px-3 py-1.5">
        <div className="flex items-end gap-0.5 h-5">
          {(row.weeks || []).map((v, i) => (
            <div key={i} className="w-3 bg-zinc-800 rounded-sm" style={{ height: `${Math.max(2, (v / maxW) * 20)}px` }} title={`${weeks[i]?.code || "W?"}: ${fmtInt(v)}`} />
          ))}
        </div>
      </td>
      <td className="px-3 py-1.5 text-right font-mono">{fmtInt(row.completed)}</td>
      <td className="px-3 py-1.5 text-right font-mono">{fmtInt(row.pending)}</td>
      <td className="px-3 py-1.5 text-right font-mono">{fmtInt(row.active_skus)}</td>
    </tr>
  );
}

// ─── Mover table (mirrors Forecast Summary) ────────────────────────
function MoverTable({ title, rows, valKey, tid, sign = "", tone = "", isDelta = false }) {
  return (
    <Card className="rounded-sm" data-testid={tid}>
      <CardHeader className="pb-2"><CardTitle className="text-sm">{title} <Badge variant="outline" className="text-[10px] ml-1 rounded-sm">{rows.length}</Badge></CardTitle></CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <div className="text-xs text-zinc-400 italic">None.</div>
        ) : (
          <ol className="text-xs space-y-1">
            {rows.slice(0, 10).map((r) => (
              <li key={r.sku_id} className="flex justify-between gap-2 border-b border-zinc-100 pb-1">
                <span className="truncate flex-1 min-w-0" title={r.description}>
                  <span className="font-mono font-semibold">{r.sku_id}</span>
                  {r.description && r.description !== r.sku_id && (
                    <span className="text-zinc-400 ml-1">· {r.description}</span>
                  )}
                </span>
                <span className={`font-mono shrink-0 ${tone}`}>
                  {isDelta ? (
                    <>{r.delta > 0 ? <ArrowRight className="inline w-3 h-3 rotate-[-45deg] text-emerald-700" /> : <ArrowRight className="inline w-3 h-3 rotate-[45deg] text-rose-700" />}
                    {" "}{fmtDelta(r.delta)}</>
                  ) : (
                    <>{sign}{fmtInt(r[valKey])}</>
                  )}
                </span>
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Deep dive — buyer SKU search + trajectory ─────────────────────
function DeepDive({ month, branch, token }) {
  const [q, setQ] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);

  // Debounced search against the existing buyer-SKU endpoint.
  useEffect(() => {
    if (!q || q.length < 2) { setSuggestions([]); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      axios.get(`${API}/api/buyer-skus?status=ACTIVE&search=${encodeURIComponent(q)}`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then((r) => {
        if (cancelled) return;
        const arr = Array.isArray(r.data) ? r.data : (r.data.items || []);
        setSuggestions(arr.slice(0, 8));
      }).catch(() => setSuggestions([]));
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, token]);

  useEffect(() => {
    if (!selected) { setPayload(null); return; }
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/api/cpc/production-summary/sku-trajectory?month=${month}&sku_id=${encodeURIComponent(selected)}&branch=${encodeURIComponent(branch)}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => {
      if (cancelled) return;
      setPayload(r.data);
    }).catch(() => setPayload(null)).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [selected, month, branch, token]);

  return (
    <Card className="rounded-sm" data-testid="ps-deep-dive">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Deep dive — buyer SKU search</CardTitle>
        <div className="text-[11px] text-zinc-500">Per-SKU weekly plan + how it has moved across the captured snapshots. Scoped by the branch filter above.</div>
      </CardHeader>
      <CardContent>
        <div className="relative">
          <div className="flex items-center gap-2 border border-zinc-200 rounded-sm px-2">
            <Search className="w-3.5 h-3.5 text-zinc-400" />
            <Input className="border-0 focus-visible:ring-0 h-8 text-xs" placeholder="Type a buyer SKU code or name…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="ps-deep-dive-search" />
          </div>
          {suggestions.length > 0 && !selected && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-zinc-200 rounded-sm shadow-sm max-h-56 overflow-y-auto">
              {suggestions.map((s) => (
                <button key={s.buyer_sku_id} className="w-full text-left px-3 py-1.5 text-xs hover:bg-zinc-50 border-b border-zinc-100"
                        onClick={() => { setSelected(s.buyer_sku_id); setQ(s.buyer_sku_id); setSuggestions([]); }}
                        data-testid={`ps-deep-dive-suggestion-${s.buyer_sku_id}`}>
                  <span className="font-mono font-semibold">{s.buyer_sku_id}</span>
                  {s.name && <span className="text-zinc-500 ml-2">· {s.name}</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        {loading && <div className="text-xs text-zinc-500 mt-2"><Loader2 className="inline w-3 h-3 animate-spin mr-1" />Loading…</div>}
        {payload && (
          <div className="mt-4 space-y-3" data-testid="ps-deep-dive-result">
            <div className="text-xs">
              <span className="font-mono font-semibold">{payload.sku_id}</span>
              {payload.description && <span className="text-zinc-500 ml-2">· {payload.description}</span>}
              <span className="ml-3 text-zinc-500">Brand: <span className="font-medium text-zinc-800">{payload.brand || "—"}</span></span>
              <span className="ml-3 text-zinc-500">Vertical: <span className="font-medium text-zinc-800">{payload.vertical || "—"}</span></span>
              {payload.resolution_source && (
                <Badge variant="outline" className="ml-2 text-[10px] rounded-sm">{payload.resolution_source}</Badge>
              )}
            </div>
            {/* Latest week plan */}
            <div>
              <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1">Latest snapshot — weekly plan</div>
              <table className="w-full text-xs">
                <thead className="bg-zinc-50">
                  <tr>
                    <th className="text-left px-2 py-1 text-[10px] uppercase text-zinc-500">Week</th>
                    <th className="text-left px-2 py-1 text-[10px] uppercase text-zinc-500">Range</th>
                    <th className="text-right px-2 py-1 text-[10px] uppercase text-zinc-500">Planned</th>
                    <th className="text-right px-2 py-1 text-[10px] uppercase text-zinc-500">Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {payload.latest_week_plan.map((w) => (
                    <tr key={w.code} className="border-t border-zinc-100">
                      <td className="px-2 py-1 font-medium">{w.code}</td>
                      <td className="px-2 py-1 font-mono text-zinc-500">{w.start} → {w.end}</td>
                      <td className="px-2 py-1 text-right font-mono">{fmtInt(w.planned)}</td>
                      <td className="px-2 py-1 text-right font-mono">{fmtInt(w.completed)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Snapshot trajectory */}
            <div>
              <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1">Snapshot trajectory — total planned per captured Monday</div>
              <ResponsiveContainer width="100%" height={120}>
                <LineChart data={payload.snapshot_trajectory}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                  <XAxis dataKey="snapshot_date" />
                  <YAxis tickFormatter={fmtInt} />
                  <Tooltip formatter={(v) => fmtInt(v)} />
                  <Line type="monotone" dataKey="total_planned" stroke={C.primary} strokeWidth={2} dot={{ r: 3, fill: C.primary }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
